import { mutationOptions } from "@tanstack/react-query";

import { authClient } from "@platform/auth/client";
import { activeMemberQueryOptions } from "@platform/auth/guards";
import { apiKeysQueryOptions } from "@platform/features/api-keys/api-keys.queries";
import { api } from "@platform/core/api";
import { queryClient } from "@platform/core/query";
import { invitationsQueryOptions, membersQueryOptions } from "./tenant.queries";
import type { Member } from "./tenant.types";

const invalidateMembers = () =>
	queryClient.invalidateQueries({ queryKey: membersQueryOptions.queryKey });

// A suspension or removal revokes the member's keys server-side, and a demotion their app
// connections, so each refreshes the key list with the roster.
const invalidateMembersAndKeys = () =>
	Promise.all([
		invalidateMembers(),
		queryClient.invalidateQueries({ queryKey: apiKeysQueryOptions.queryKey }),
	]);

async function updateMemberRole(memberId: string, role: Member["role"]) {
	const { error } = await authClient.organization.updateMemberRole({ memberId, role });
	// Better Auth reports a refusal, for example the last-owner guard, in the result.
	if (error) throw new Error(error.message ?? error.statusText);
}

// A tenant holds one owner, so a transfer promotes the target and steps the acting owner down.
export const changeMemberRoleMutation = mutationOptions({
	mutationFn: async (input: {
		memberId: string;
		role: Member["role"];
		stepDownMemberId: string | null;
	}) => {
		await updateMemberRole(input.memberId, input.role);
		if (input.stepDownMemberId) await updateMemberRole(input.stepDownMemberId, "admin");
	},
	// A change that fails can still have landed in part, and a step-down changes the actor's own role.
	onSettled: () =>
		Promise.all([
			invalidateMembersAndKeys(),
			queryClient.invalidateQueries({ queryKey: activeMemberQueryOptions.queryKey }),
		]),
});

export const suspendMemberMutation = mutationOptions({
	mutationFn: (id: string) => api.post(`/platform/members/${id}/suspend`),
	onSuccess: invalidateMembersAndKeys,
});

export const reactivateMemberMutation = mutationOptions({
	mutationFn: (id: string) => api.post(`/platform/members/${id}/reactivate`),
	onSuccess: invalidateMembers,
});

export const removeMemberMutation = mutationOptions({
	mutationFn: async (memberIdOrEmail: string) => {
		const { error } = await authClient.organization.removeMember({
			memberIdOrEmail,
		});
		if (error) throw error;
	},
	onSuccess: invalidateMembersAndKeys,
});

export const cancelInvitationMutation = mutationOptions({
	mutationFn: async (invitationId: string) => {
		const { error } = await authClient.organization.cancelInvitation({
			invitationId,
		});
		if (error) throw error;
	},
	onSuccess: () =>
		queryClient.invalidateQueries({
			queryKey: invitationsQueryOptions.queryKey,
		}),
});
